import { FC, Key, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Input, Modal, Row, Space, Tree } from 'antd';
import type { DataNode, TreeProps } from 'antd/es/tree';
import axios from 'axios';
import { LucideListEnd } from 'lucide-react';
import {
    TbArrowRight,
    TbArrowUpRight,
    TbChevronDown,
    TbCopy,
    TbForms,
    TbSquareCheckFilled,
    TbWebhook,
    TbWorldBolt,
    TbX,
} from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EmptySection, LearnMoreFooter, MiddleDot, OverflowFadeScrollArea, SmallTag } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { isProduction } from '@/shared/constants';
import { SetStateMethod, Webhook } from '@/types';

interface WebhookEventOption {
    id: string;
    label: string;
}

interface WebhookEventGroup {
    name: string;
    options: WebhookEventOption[];
}

interface WebhookEventsResponse {
    count: number;
    data: WebhookEventGroup[];
}

interface CreateWebhookModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    onCreated?: (webhook: Webhook) => void;
}

function isValidWebhookEndpoint(value: string) {
    try {
        const protocol = new URL(value).protocol;

        return protocol === 'https:' || (!isProduction && protocol === 'http:');
    } catch {
        return false;
    }
}

function getErrorMessage(error: unknown) {
    if (!axios.isAxiosError(error)) {
        return 'Please try again later.';
    }

    return error.response?.data?.error?.message || 'Please verify the endpoint and try again.';
}

const CreateWebhookModal: FC<CreateWebhookModalProps> = ({ open, setOpen, onCreated }) => {
    const { token } = useAntDesignTheme();
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [creating, setCreating] = useState(false);
    const [eventGroups, setEventGroups] = useState<WebhookEventGroup[]>([]);
    const [name, setName] = useState('');
    const [endpoint, setEndpoint] = useState('');
    const [checkedKeys, setCheckedKeys] = useState<Key[]>([]);

    const reset = useCallback(() => {
        setName('');
        setEndpoint('');
        setCheckedKeys([]);
    }, []);

    const close = () => {
        reset();
        setOpen(false);
    };

    const getWebhookEvents = useCallback(async () => {
        try {
            setLoadingEvents(true);
            const { data } = await authorizedAxiosInstance.get<WebhookEventsResponse>('/api/webhooks/events');
            setEventGroups(Array.isArray(data.data) ? data.data : []);
        } catch (error) {
            captureException(error);
            setEventGroups([]);
            toast.error('Unable to load webhook events', { description: getErrorMessage(error) });
        } finally {
            setLoadingEvents(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            void getWebhookEvents();
        }
    }, [getWebhookEvents, open]);

    const supportedEventTypes = useMemo(
        () => new Set(eventGroups.flatMap(({ options }) => options.map(({ id }) => id))),
        [eventGroups],
    );
    const selectedEventTypes = useMemo(
        () => checkedKeys.map(String).filter((key) => supportedEventTypes.has(key)),
        [checkedKeys, supportedEventTypes],
    );

    const treeData = useMemo<DataNode[]>(
        () =>
            eventGroups.map((group) => ({
                key: `group:${group.name}`,
                title: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                        <span>{group.name}</span>
                        <SmallTag compact message={group.options.length} styles={{ marginLeft: 0 }} />
                    </span>
                ),
                children: group.options.map((option) => ({
                    key: option.id,
                    title: (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                            <span>{option.label}</span>
                            <Button
                                aria-label={`Copy ${option.id}`}
                                size='small'
                                type='text'
                                icon={<TbCopy size='1rem' />}
                                style={{ color: token.colorTextTertiary }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void navigator.clipboard.writeText(option.id);
                                    toast('Webhook Event Type Copied', { description: option.id });
                                }}
                            />
                        </span>
                    ),
                })),
            })),
        [eventGroups, token.colorTextTertiary],
    );

    const handleCheck: TreeProps['onCheck'] = (keys) => {
        setCheckedKeys(Array.isArray(keys) ? keys : keys.checked);
    };

    const handleCreateWebhook = async () => {
        const trimmedName = name.trim();
        const trimmedEndpoint = endpoint.trim();

        if (!trimmedName) {
            toast.error('Webhook name is required');
            return;
        }
        if (!isValidWebhookEndpoint(trimmedEndpoint)) {
            toast.error(isProduction ? 'Enter a valid HTTPS endpoint' : 'Enter a valid HTTP or HTTPS endpoint');
            return;
        }
        if (selectedEventTypes.length === 0) {
            toast.error('Select at least one webhook event');
            return;
        }

        try {
            setCreating(true);
            const { data } = await authorizedAxiosInstance.post<Webhook>('/api/webhooks', {
                name: trimmedName,
                endpoint: trimmedEndpoint,
                filterTypes: selectedEventTypes,
            });

            onCreated?.(data);
            close();
        } catch (error) {
            captureException(error);
            toast.error('Unable to create webhook', { description: getErrorMessage(error) });
        } finally {
            setCreating(false);
        }
    };

    const canCreate =
        Boolean(name.trim()) &&
        isValidWebhookEndpoint(endpoint.trim()) &&
        selectedEventTypes.length > 0 &&
        !loadingEvents;

    return (
        <Modal
            open={open}
            mask={false}
            title='Add Webhook'
            onCancel={close}
            style={{ minWidth: '55rem' }}
            footer={
                <Row justify='space-between' align='middle' gutter={[12, 12]}>
                    <Col flex='auto'>
                        <LearnMoreFooter
                            align='left'
                            href='https://docs.countercyclical.io/developers/webhooks'
                            topic='Webhooks'
                        />
                    </Col>
                    <Col flex='none'>
                        <Button onClick={close}>Cancel</Button>
                        <Button
                            type='primary'
                            style={{ marginLeft: 8 }}
                            onClick={() => void handleCreateWebhook()}
                            disabled={!canCreate}
                            loading={creating}
                        >
                            Create Webhook
                            <TbArrowRight size='1rem' />
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Form layout='vertical' requiredMark={false}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item style={{ marginBottom: '0.5rem' }} label='Name'>
                            <Input
                                prefix={<TbForms style={{ color: token.colorTextQuaternary }} />}
                                placeholder='Permission updates...'
                                value={name}
                                maxLength={250}
                                onChange={(event) => setName(event.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            style={{ marginBottom: '0.5rem' }}
                            label={
                                <>
                                    Webhook Endpoint
                                    <MiddleDot horizontalMargin='0.5rem' />
                                    Test with{' '}
                                    <a href='https://www.svix.com/play/' target='_blank' rel='noopener noreferrer'>
                                        Svix Play <TbArrowUpRight style={{ verticalAlign: '-0.125rem' }} />
                                    </a>
                                </>
                            }
                        >
                            <Input
                                status={endpoint && !isValidWebhookEndpoint(endpoint.trim()) ? 'error' : undefined}
                                prefix={<TbWorldBolt style={{ color: token.colorTextQuaternary }} />}
                                placeholder='https://example.com/webhooks/countercyclical...'
                                value={endpoint}
                                onChange={(event) => setEndpoint(event.target.value)}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation='left' style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                    <small>Subscribe to</small>
                </Divider>

                <Row justify='space-between' align='middle' style={{ marginBottom: '0.5rem' }}>
                    <Col flex='auto'>
                        <small style={{ color: token.colorTextTertiary }}>
                            <LucideListEnd
                                size='0.75rem'
                                style={{ transform: 'rotateY(180deg)', verticalAlign: '-0.125rem', marginRight: 6 }}
                            />
                            {eventGroups.length} Groups
                            <MiddleDot horizontalMargin='0.5rem' />
                            {supportedEventTypes.size} Event Types
                        </small>
                    </Col>
                    <Col flex='none'>
                        <Space.Compact>
                            <Button size='small' style={{ pointerEvents: 'none' }}>
                                <TbSquareCheckFilled style={{ color: token.colorTextSecondary }} />
                                {selectedEventTypes.length} Selected
                            </Button>
                            <Button
                                aria-label='Clear selected webhook events'
                                size='small'
                                type='dashed'
                                icon={<TbX style={{ color: token.colorTextSecondary }} />}
                                disabled={checkedKeys.length === 0}
                                onClick={() => setCheckedKeys([])}
                            />
                        </Space.Compact>
                    </Col>
                </Row>

                {loadingEvents ? (
                    <BaseSkeleton active paragraph={{ rows: 12 }} />
                ) : eventGroups.length === 0 ? (
                    <EmptySection
                        height={16 * 10}
                        icon={<TbWebhook size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                        label='No Webhook Events'
                        description='Webhook events will appear here when they are available.'
                    />
                ) : (
                    <OverflowFadeScrollArea
                        aria-label='Webhook event subscriptions'
                        containerStyle={{
                            maxHeight: '25rem',
                            border: `1px dashed ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                        }}
                        style={{ padding: '0.75rem 0.5rem' }}
                        surface='elevated'
                    >
                        <Tree
                            checkable
                            defaultExpandAll
                            selectable={false}
                            showLine
                            switcherIcon={({ expanded }) => (
                                <TbChevronDown
                                    style={{
                                        transform: expanded ? undefined : 'rotate(-90deg)',
                                        color: token.colorTextTertiary,
                                    }}
                                />
                            )}
                            treeData={treeData}
                            checkedKeys={checkedKeys}
                            onCheck={handleCheck}
                        />
                    </OverflowFadeScrollArea>
                )}
            </Form>
        </Modal>
    );
};

export default CreateWebhookModal;
